import { useEffect, useState } from "react";
type Draft = { id: string; title: string; updated_at: string };
type Campaign = {
  id: string;
  draft_id: string;
  title: string;
  body: string;
  category: string;
  target_label: string;
  status: string;
  matched_accounts: number;
  created_at: string;
  prepared_at: string | null;
  last_error: string | null;
};
type Summary = {
  campaign: Campaign;
  accounts_with_devices: number;
  devices: number;
  accepted: number;
  accepted_accounts: number;
  failed: number;
  uncertain: number;
  excluded: number;
  pending: number;
};
type Props = {
  drafts: Draft[];
  request: (body: Record<string, unknown>) => Promise<any>;
};
const labels: Record<string, string> = {
  preparing: "Préparation de l’audience",
  ready: "Prête à confirmer",
  sending: "Envoi en cours",
  paused: "En pause — vérification requise",
  completed: "Terminée",
  cancelled: "Arrêtée",
};
export function Campaigns({ drafts, request }: Props) {
  const [rows, setRows] = useState<Summary[]>([]);
  const [draftId, setDraftId] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  async function refresh() {
    setRows((await request({ action: "campaign_list" })).campaigns);
  }
  useEffect(() => {
    void refresh().catch((e) => setError(e.message));
  }, []);
  async function action(body: Record<string, unknown>, message: string) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await request(body);
      setConfirmation("");
      await refresh();
      setNotice(message);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Service indisponible.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="mt-8 space-y-5 rounded-2xl border border-black/10 bg-white p-6">
      <header className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">Campagnes complètes</h2>
        <button
          disabled={busy}
          className="text-sm underline"
          onClick={() =>
            void action({ action: "campaign_list" }, "Historique actualisé.")}
        >
          Actualiser
        </button>
      </header>
      <p className="text-sm text-neutral-600">
        Préparez l’audience d’un brouillon enregistré. Aucun message ne part
        avant votre confirmation. L’envoi continue même si vous fermez le BO.
      </p>
      {error && (
        <p
          role="alert"
          className="rounded-lg bg-red-50 p-3 text-sm text-red-800"
        >
          {error}
        </p>
      )}
      {notice && (
        <p
          role="status"
          className="rounded-lg bg-green-50 p-3 text-sm text-green-800"
        >
          {notice}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <select
          aria-label="Brouillon de la campagne"
          className="min-w-0 flex-1 rounded-xl border border-black/15 p-3"
          value={draftId}
          onChange={(e) => setDraftId(e.target.value)}
        >
          <option value="">Choisir un brouillon enregistré</option>
          {drafts.filter((d) => !rows.some((r) => r.campaign.draft_id === d.id))
            .map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
        </select>
        <button
          disabled={busy || !draftId}
          className="rounded-xl bg-yellow-200 px-4 py-3 font-medium disabled:opacity-40"
          onClick={() => {
            const d = drafts.find((d) => d.id === draftId);
            if (d) {
              void action(
                {
                  action: "campaign_prepare",
                  id: d.id,
                  updated_at: d.updated_at,
                },
                "Préparation lancée. Actualisez pour consulter l’audience ; aucun envoi n’est déclenché.",
              );
            }
          }}
        >
          Préparer l’audience
        </button>
      </div>
      <p className="text-xs text-neutral-500">
        Seuls les comptes ayant activé les notifications sont sélectionnés.
        L’audience est une estimation : les préférences et appareils sont
        revérifiés pendant l’envoi. Une campagne par brouillon ; créez un
        nouveau brouillon pour une autre campagne.
      </p>
      <div className="space-y-4">
        {rows.map((r) => {
          const c = r.campaign;
          const expired = c.prepared_at !== null &&
            Date.now() - Date.parse(c.prepared_at) > 86400000;
          const eligible = r.devices - r.excluded;
          return (
            <article
              key={c.id}
              className="space-y-3 rounded-xl border border-black/10 p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <h3 className="font-semibold">{c.title}</h3>
                <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs">
                  {labels[c.status] || c.status}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm">{c.body}</p>
              <p className="text-xs text-neutral-500">
                {c.target_label} ·{" "}
                {new Date(c.created_at).toLocaleString("fr-FR")}
              </p>
              <div className="grid grid-cols-2 gap-3 rounded-lg bg-neutral-50 p-3 text-sm md:grid-cols-4">
                <div>
                  <strong>{c.matched_accounts}</strong>
                  <p>Comptes consentants trouvés</p>
                </div>
                <div>
                  <strong>{r.accounts_with_devices}</strong>
                  <p>Comptes avec appareil</p>
                </div>
                <div>
                  <strong>{c.status === "ready" ? eligible : r.devices}</strong>
                  <p>
                    Appareils{" "}
                    {c.status === "ready" ? "ciblés" : "traités ou à traiter"}
                  </p>
                </div>
                <div>
                  <strong>{r.accepted}</strong>
                  <p>Envois acceptés · {r.accepted_accounts} comptes</p>
                </div>
              </div>
              <p className="text-xs text-neutral-600">
                {r.pending} restant(s) · {r.failed} échec(s) · {r.uncertain}
                {" "}
                résultat(s) incertain(s) · {r.excluded}{" "}
                exclu(s). L’acceptation par Firebase ne prouve pas la réception.
              </p>
              {c.last_error && (
                <p role="alert" className="text-sm text-red-700">
                  {c.last_error}
                </p>
              )}
              {c.status === "ready" && (
                <div className="space-y-3 border-t border-black/10 pt-3">
                  {expired
                    ? (
                      <p className="text-sm text-amber-800">
                        Estimation expirée après 24 h. Arrêtez cette campagne et
                        préparez un nouveau brouillon.
                      </p>
                    )
                    : eligible === 0
                    ? (
                      <p className="text-sm text-amber-800">
                        Aucun appareil éligible. Aucun envoi possible.
                      </p>
                    )
                    : (
                      <>
                        <label className="flex gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={confirmation === c.id}
                            onChange={(e) =>
                              setConfirmation(e.target.checked ? c.id : "")}
                          />Je confirme l’envoi de ce message à environ{" "}
                          {eligible} appareils ayant activé les notifications.
                        </label>
                        <button
                          disabled={busy || confirmation !== c.id}
                          className="rounded-xl bg-neutral-900 px-4 py-3 text-sm font-medium text-white disabled:opacity-40"
                          onClick={() =>
                            void action(
                              {
                                action: "campaign_confirm",
                                id: c.id,
                                confirm: true,
                              },
                              "Campagne confirmée. L’envoi se poursuit en arrière-plan.",
                            )}
                        >
                          Envoyer la campagne
                        </button>
                      </>
                    )}
                </div>
              )}
              {["preparing", "ready", "sending", "paused"].includes(c.status) &&
                (
                  <button
                    disabled={busy}
                    className="text-sm underline"
                    onClick={() =>
                      void action(
                        { action: "campaign_cancel", id: c.id },
                        "Campagne arrêtée. Les messages déjà confiés à Firebase ne peuvent pas être rappelés.",
                      )}
                  >
                    Arrêter la campagne
                  </button>
                )}
              {c.status === "paused" && (
                <button
                  disabled={busy}
                  className="ml-4 text-sm underline"
                  onClick={() =>
                    void action(
                      { action: "campaign_resume", id: c.id },
                      "Reprise demandée. Les résultats incertains ne seront pas renvoyés.",
                    )}
                >
                  Reprendre après vérification
                </button>
              )}
            </article>
          );
        })}
      </div>
      {!rows.length && (
        <p className="text-sm text-neutral-500">
          Aucune campagne pour le moment.
        </p>
      )}
    </section>
  );
}
